import { Flag, Register } from "../../interfaces/op-code";
import { Cpu } from "./cpu";
import { Logger } from "../../util/logger";

const logger = new Logger("INS", 0); // Module-level logger with INS prefix, default log level 0

export function getInstructionExecutor(cpu: Cpu, instruction: string): () => void {
    logger.log(1, () => `Mapping instruction: ${instruction}`);
    const instructionMap: { [key: string]: () => void } = {
        ADC: () => cpu.addWithCarry(),
        SBC: () => cpu.subtractWithCarry(),
        AND: () => cpu.logicalOperation((a, b) => a & b),
        ASL: () => cpu.arithmeticShiftLeft(),
        BIT: () => cpu.bitTest(),
        CMP: () => cpu.compare(Register.A),
        CPX: () => cpu.compare(Register.X),
        CPY: () => cpu.compare(Register.Y),
        DEC: () => cpu.decrementMemory(),
        EOR: () => cpu.logicalOperation((a, b) => a ^ b),
        INC: () => cpu.incrementMemory(),
        LSR: () => cpu.logicalShiftRight(),
        ORA: () => cpu.logicalOperation((a, b) => a | b),
        ROL: () => cpu.rotateLeft(),
        ROR: () => cpu.rotateRight(),
        LDA: () => cpu.load(Register.A),
        LDX: () => cpu.load(Register.X),
        LDY: () => cpu.load(Register.Y),
        STA: () => cpu.store(Register.A),
        STX: () => cpu.store(Register.X),
        STY: () => cpu.store(Register.Y),
        TAX: () => cpu.transfer(Register.A, Register.X),
        TAY: () => cpu.transfer(Register.A, Register.Y),
        TSX: () => cpu.transfer(Register.SP, Register.X),
        TXA: () => cpu.transfer(Register.X, Register.A),
        TXS: () => cpu.transfer(Register.X, Register.SP, false),
        TYA: () => cpu.transfer(Register.Y, Register.A),
        DEX: () => cpu.decrement(Register.X),
        DEY: () => cpu.decrement(Register.Y),
        INX: () => cpu.increment(Register.X),
        INY: () => cpu.increment(Register.Y),
        CLC: () => cpu.setFlag(Flag.Carry, false),
        CLD: () => cpu.setFlag(Flag.Decimal, false),
        CLI: () => cpu.setFlag(Flag.InterruptDisable, false),
        CLV: () => cpu.setFlag(Flag.Overflow, false),
        SEC: () => cpu.setFlag(Flag.Carry, true),
        SED: () => cpu.setFlag(Flag.Decimal, true),
        SEI: () => cpu.setFlag(Flag.InterruptDisable, true),
        JMP: () => cpu.jump(),
        JSR: () => cpu.jumpSubroutine(),
        RTS: () => cpu.returnSubroutine(),
        RTI: () => cpu.returnInterrupt(),
        BRK: () => cpu.break(),
        NOP: () => cpu.noOperation(),
        BCC: () => cpu.branch(Flag.Carry, false),
        BCS: () => cpu.branch(Flag.Carry, true),
        BEQ: () => cpu.branch(Flag.Zero, true),
        BMI: () => cpu.branch(Flag.Negative, true),
        BNE: () => cpu.branch(Flag.Zero, false),
        BPL: () => cpu.branch(Flag.Negative, false),
        BVC: () => cpu.branch(Flag.Overflow, false),
        BVS: () => cpu.branch(Flag.Overflow, true),
        PHA: () => cpu.pushRegister(Register.A),
        PLA: () => cpu.pullRegister(Register.A),
        PHP: () => cpu.pushStatus(),
        PLP: () => cpu.pullStatus(),
        // Unofficial opcodes
        LAX: () => cpu.loadAandX(),
        SAX: () => cpu.storeAandX(),
        DCP: () => cpu.decrementAndCompare(),
        ISC: () => cpu.incrementAndSubtract(),
        SLO: () => cpu.shiftLeftOr(),
        SRE: () => cpu.shiftRightEor(),
        RLA: () => cpu.rotateLeftAnd(),
        RRA: () => cpu.rotateRightAdd(),
        ARR: () => cpu.andRotateRight(),
        AXS: () => cpu.compareXandA(),
        AHX: () => cpu.storeAandXHigh(),
        SHY: () => cpu.storeYHigh(),
        SHX: () => cpu.storeXHigh(),
        TAS: () => cpu.transferAandXtoS(),
        LAS: () => cpu.loadAandXandS(),
        ANC: () => cpu.andWithCarry(),
        ALR: () => cpu.andShiftRight(),
        XAA: () => cpu.transferXandAnd(),
        SXA: () => cpu.storeXandAHigh(),
        SYA: () => cpu.storeYandAHigh(),
    };

    const fn = instructionMap[instruction];
    if (!fn) {
        logger.log(1, () => `Invalid instruction: ${instruction}`);
        return () => logger.log(1, () => `Attempted to execute invalid instruction: ${instruction}`);
    }
    logger.log(3, () => `Mapped ${instruction} to CPU method`);
    return fn;
}